import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip } from "@mui/material";
import DataTable from "bundles/Internal/components/common/DataTable";
import SuggestionTableFilters from "bundles/Internal/components/suggestion/SuggestionTableFilters";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "candidate_name", label: "Candidate" },
  { key: "company_name", label: "Company" },
  { key: "status", label: "Status" },
  { key: "selected_by", label: "Selected by" },
  { key: "created_at", label: "Created at" },
];

const STATUS_COLOR = {
  pending: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  saved: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  rejected: { bg: "rgba(211,47,47,0.10)", text: "#d32f2f" },
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

function formatSuggestionCell(suggestion, key) {
  const value = suggestion?.[key];

  if (key === "status" && value) {
    const c = STATUS_COLOR[value] || STATUS_COLOR.pending;
    return (
      <Chip
        label={value}
        size="small"
        sx={{
          backgroundColor: c.bg,
          color: c.text,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 11,
          fontWeight: 600,
          borderRadius: "8px",
          height: 24,
        }}
      />
    );
  }

  if (key === "created_at") return formatDateTime(value);

  if (value === null || value === undefined) return "";
  return String(value);
}

const SuggestionTable = ({
  suggestions = [],
  onQueryChange,
  filters,
  onFiltersChange,
  onAddClick,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const renderCell = useCallback((suggestion, key) => {
    if (key === "candidate_name") {
      const value = suggestion?.candidate_name;
      if (!value) return "";
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (suggestion.candidate_id) navigate(`/internal/candidates/${suggestion.candidate_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }
    if (key === "company_name") {
      const value = suggestion?.company_name;
      if (!value) return "";
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (suggestion.company_id) navigate(`/internal/companies/${suggestion.company_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }
    return formatSuggestionCell(suggestion, key);
  }, [navigate]);

  return (
    <DataTable
      title="Suggestions"
      totalLabel={(count) => `${count.toLocaleString()} total suggestions`}
      emptyTitle="No suggestions found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={suggestions}
      renderCell={renderCell}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by candidate or company name..."
      searchInfo="Candidate name, candidate email, company"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <SuggestionTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      onAddClick={onAddClick}
      addButtonLabel="Add Suggestion"
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
    />
  );
};

export default SuggestionTable;
